import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Bookmark, Trash2, Quote, Pencil, X, Check } from "lucide-react";
import { toast } from "sonner";
import SmartImage from "@/components/SmartImage";

export default function Highlights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // {id, note}

  const load = () => {
    api.get("/highlights")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    // Optimistic
    setItems((cur) => cur.filter((h) => h.id !== id));
    try {
      await api.delete(`/highlights/${id}`);
      toast("Highlight removed");
    } catch {
      toast.error("Could not remove");
      load();
    }
  };

  const saveNote = async () => {
    if (!editing) return;
    try {
      await api.patch(`/highlights/${editing.id}`, { note: editing.note });
      setItems((cur) => cur.map((h) => h.id === editing.id ? { ...h, note: editing.note } : h));
      setEditing(null);
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    }
  };

  return (
    <div data-testid="highlights-page" className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="label-eyebrow text-sage mb-3">Your commonplace book</div>
      <h1 className="font-serif text-5xl sm:text-6xl tracking-tight">Highlights.</h1>
      <p className="text-muted_ink mt-4 max-w-xl">
        Passages you saved while reading. Yours alone — nothing here is shared.
      </p>

      <div className="mt-14">
        {loading ? (
          <div className="text-muted_ink">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="highlights-empty" className="border border-edge p-16 text-center">
            <Bookmark className="w-8 h-8 text-terracotta mx-auto mb-4" strokeWidth={1.25} />
            <div className="font-serif text-3xl italic mb-3">Nothing saved yet.</div>
            <p className="text-muted_ink mb-6 max-w-md mx-auto">
              While you're reading, tap "Save a highlight" to keep a passage. It'll live here forever.
            </p>
            <Link to="/library" className="btn-primary">Open my library</Link>
          </div>
        ) : (
          <div className="space-y-6">
            {items.map((h) => {
              const cover = h.cover_url || (h.cover_path ? `${API}/files/${h.cover_path}` : null);
              return (
                <div key={h.id} data-testid={`highlight-${h.id}`} className="border border-edge bg-white p-6 md:p-8 flex gap-5">
                  <Link to={`/book/${h.book_id}`} className="w-16 md:w-20 h-24 md:h-28 bg-linen flex-shrink-0 overflow-hidden">
                    {cover && <SmartImage src={cover} alt={h.book_title} className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <Quote className="w-4 h-4 text-terracotta flex-shrink-0 mt-1.5" strokeWidth={1.5} />
                      <blockquote className="font-serif text-lg md:text-xl italic leading-relaxed text-ink">
                        {h.quote}
                      </blockquote>
                    </div>
                    <div className="mt-3 text-sm text-muted_ink">
                      — from <Link to={`/book/${h.book_id}`} className="text-ink underline underline-offset-2 decoration-terracotta hover:text-terracotta">{h.book_title}</Link>
                      {h.book_author && <span>, {h.book_author}</span>}
                    </div>

                    {editing?.id === h.id ? (
                      <div className="mt-4 border-t border-edge pt-4 space-y-2">
                        <textarea
                          data-testid={`edit-note-${h.id}`}
                          value={editing.note}
                          onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                          rows={3}
                          className="w-full border border-edge bg-linen px-3 py-2 text-sm focus:outline-none focus:border-ink"
                          placeholder="Add a note to remember why this mattered"
                        />
                        <div className="flex gap-2">
                          <button onClick={saveNote} className="text-xs uppercase tracking-widest border border-ink px-3 py-1.5 hover:bg-ink hover:text-paper transition-colors inline-flex items-center gap-1">
                            <Check className="w-3 h-3" /> Save
                          </button>
                          <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-widest px-3 py-1.5 text-muted_ink hover:text-ink inline-flex items-center gap-1">
                            <X className="w-3 h-3" /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      h.note ? (
                        <div className="mt-4 border-t border-edge pt-4 text-sm text-muted_ink italic">
                          Note: {h.note}
                        </div>
                      ) : null
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      data-testid={`edit-${h.id}`}
                      onClick={() => setEditing({ id: h.id, note: h.note || "" })}
                      className="text-muted_ink hover:text-ink"
                      title="Edit note"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`remove-${h.id}`}
                      onClick={() => remove(h.id)}
                      className="text-muted_ink hover:text-terracotta"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
